import React from 'react';
import { BookOpen, Music, Sparkles, FileText, Play } from 'lucide-react';
import { Tab } from '../types';
import { useLanguage } from './LanguageContext';

interface Props {
  activeTab: Tab;
  onNavigate: (tab: Tab) => void;
}

export const QuickAccessBar: React.FC<Props> = ({ activeTab, onNavigate }) => {
  const { t } = useLanguage();
  
  const quickAccessItems = [
    { 
      id: 'ebooks', 
      labelKey: 'quick_ebooks' as const,
      tab: Tab.EBOOKS, 
      icon: BookOpen,
      gradient: 'from-orange-500 to-amber-500',
      borderColor: 'border-orange-300 dark:border-orange-700',
      activeBg: 'bg-orange-100 dark:bg-orange-900/40',
      activeRing: 'ring-orange-500'
    },
    { 
      id: 'audios', 
      labelKey: 'quick_audios' as const,
      tab: Tab.SATSANG_AUDIOS, 
      icon: Music,
      gradient: 'from-purple-500 to-pink-500',
      borderColor: 'border-purple-300 dark:border-purple-700',
      activeBg: 'bg-purple-100 dark:bg-purple-900/40',
      activeRing: 'ring-purple-500'
    },
    { 
      id: 'videos', 
      labelKey: 'quick_videos' as const,
      tab: Tab.VIDEOS, 
      icon: Play,
      gradient: 'from-red-500 to-orange-500',
      borderColor: 'border-red-300 dark:border-red-700',
      activeBg: 'bg-red-100 dark:bg-red-900/40',
      activeRing: 'ring-red-500'
    },
    { 
      id: 'ai_vani', 
      labelKey: 'quick_ai_vani' as const,
      tab: Tab.AI_SANT_VANI, 
      icon: Sparkles,
      gradient: 'from-templeRed to-rose-500',
      borderColor: 'border-red-300 dark:border-red-700',
      activeBg: 'bg-red-100 dark:bg-red-900/40',
      activeRing: 'ring-templeRed'
    },
    { 
      id: 'blogs', 
      labelKey: 'quick_blogs' as const,
      tab: Tab.BLOGS, 
      icon: FileText,
      gradient: 'from-emerald-500 to-teal-500',
      borderColor: 'border-emerald-300 dark:border-emerald-700',
      activeBg: 'bg-emerald-100 dark:bg-emerald-900/40',
      activeRing: 'ring-emerald-500'
    },
  ];
  
  return (
    <div className="sticky top-14 z-40 bg-cream/95 dark:bg-stone-900/95 backdrop-blur-sm border-b border-saffron/10 dark:border-stone-800">
      <div className="max-w-7xl mx-auto px-2 py-1">
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1 -mb-1 scroll-smooth snap-x snap-mandatory">
          {quickAccessItems.map((item) => {
            const isActive = activeTab === item.tab;
            const Icon = item.icon;
            
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.tab)}
                data-testid={`quick-access-${item.id}`}
                className={`
                  py-1.5 px-3 rounded-full flex items-center gap-1.5
                  transition-all duration-200 whitespace-nowrap flex-shrink-0 snap-start
                  ${isActive 
                    ? `bg-gradient-to-r ${item.gradient} text-white shadow-md scale-105 font-bold` 
                    : `bg-white dark:bg-stone-800 text-gray-500 dark:text-stone-400 border border-gray-200 dark:border-stone-700 hover:border-gray-300 dark:hover:border-stone-600 hover:text-gray-700`
                  }
                  active:scale-95
                `}
              >
                <Icon size={14} className={isActive ? 'text-white' : ''} />
                <span className="text-[10px] sm:text-xs font-medium">
                  {t(item.labelKey)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
